/* eslint-disable complexity */
import React from "react";

import { PitchInformationPublisherDeveloperDataType } from "../../../../models/forms";
import { LocalProgramSubmitTypes } from "../../../../models/pitchProgramSubmitTypes";
import {
  AlreadySignedUpIdXboxOptions,
  HavePublisherOptions,
  KitAlreadyOptions,
  LaunchedForConsoleOptions,
  StudioSizeOptions,
} from "../../../../models/pitchUxModels";
import { ReadOnlyFormItem } from "../appInfoModelsAndHelpers";
import { ReadOnlyForm } from "../ReadOnlyForm";

type ReadOnlyPitchPublisherDeveloperProps = {
  dataInForm: PitchInformationPublisherDeveloperDataType;
};

const programConsiderationTypeMap = new Map<string, string>([
  [LocalProgramSubmitTypes.xboxGamePass, "pitchPublisherDeveloperProgramOptionThreeTitle"],
  [
    LocalProgramSubmitTypes.developerAccelerationProgramPorting,
    "pitchPublisherDeveloperProgramOptionOneTitle",
  ],
  [
    LocalProgramSubmitTypes.developerAccelerationProgramPrototype,
    "pitchPublisherDeveloperProgramOptionTwoTitle",
  ],
]);

const studioSizeMap = new Map<string, string>([
  [StudioSizeOptions.OneToFour, "pitchPublisherDeveloperProgramStudioSizeOneToFourthTitle"],
  [StudioSizeOptions.FiveToTen, "pitchPublisherDeveloperProgramStudioSizeFiveToTenTitle"],
  [StudioSizeOptions.TenToFifteen, "pitchPublisherDeveloperProgramStudioSizeTenToFifteenTitle"],
  [
    StudioSizeOptions.FifteenToTwentyFive,
    "pitchPublisherDeveloperProgramStudioSizeFifteenToTwentyfiveTitle",
  ],
  [
    StudioSizeOptions.TwentyFiveToFourtyFive,
    "pitchPublisherDeveloperProgramStudioSizeTwentyfiveToFortyfiveTitle",
  ],
  [
    StudioSizeOptions.FourtyFiveToAbove,
    "pitchPublisherDeveloperProgramStudioSizeFortyfiveToAboveTitle",
  ],
]);

const youHavePublisherMap = new Map<string, string>([
  [HavePublisherOptions.Yes, "pitchPublisherDeveloperProgramYouHavePublisherOptionYesOption"],
  [HavePublisherOptions.No, "pitchPublisherDeveloperProgramYouHavePublisherOptionNoOption"],
  [
    HavePublisherOptions.Negotiating,
    "pitchPublisherDeveloperProgramYouHavePublisherOptionNegotiatingOption",
  ],
  [HavePublisherOptions.Unsure, "pitchPublisherDeveloperProgramYouHavePublisherOptionUnsureOption"],
  [
    HavePublisherOptions.SelfPublishing,
    "pitchPublisherDeveloperProgramYouHavePublisherOptionSelfPublishingOption",
  ],
]);

const alreadySignedToXboxMap = new Map<string, string>([
  [
    AlreadySignedUpIdXboxOptions.Yes,
    "pitchPublisherDeveloperProgramYouHavePublisherOptionYesOption",
  ],
  [AlreadySignedUpIdXboxOptions.No, "pitchPublisherDeveloperProgramYouHavePublisherOptionNoOption"],
]);

const kitAlreadyMap = new Map<string, string>([
  [KitAlreadyOptions.Yes, "pitchPublisherDeveloperProgramKitAlreadyYesOption"],
  [KitAlreadyOptions.No, "pitchPublisherDeveloperProgramKitAlreadyNoOption"],
  [KitAlreadyOptions.Unknown, "pitchPublisherDeveloperProgramKitAlreadyUnknownOption"],
]);

const launchedForConsoleMap = new Map<string, string>([
  [LaunchedForConsoleOptions.Yes, "yes"],
  [LaunchedForConsoleOptions.No, "no"],
]);

const PitchPublisherDeveloperConfig: Map<string, ReadOnlyFormItem> = new Map([
  ["nameCompanyOrganization", { title: "pitchPublisherDeveloperNameCompanyOrganizationTitle" }],
  ["emailAddress", { title: "pitchPublisherDeveloperEmailAddressTitle" }],
  ["xboxContact", { title: "pitchPublisherDeveloperXboxContactTitle" }],
  [
    "programConsiderationType",
    {
      title: "pitchPublisherDeveloperProgramConsiderationTitle",
      mapper: (
        data: PitchInformationPublisherDeveloperDataType,
        localizedStrings: Map<string, string>
      ) =>
        data.programConsiderationType.map(element =>
          localizedStrings.get(programConsiderationTypeMap.get(element) ?? "")
        ),
    },
  ],
  ["reasonOfGoodFit", { title: "pitchPublisherDeveloperProgramReasonTitle" }],
  [
    "specificAspectToPrototype",
    { title: "pitchPublisherDeveloperProgramSpecificAspectPrototypeTitle" },
  ],
  [
    "studioSize",
    {
      title: "pitchPublisherDeveloperProgramStudioSizeTitle",
      mapper: (
        data: PitchInformationPublisherDeveloperDataType,
        localizedStrings: Map<string, string>
      ) => localizedStrings.get(studioSizeMap.get(data.studioSize) ?? ""),
    },
  ],
  ["studioPointOfContact", { title: "pitchPublisherDeveloperProgramStudioPointOfContactTitle" }],
  ["countryLocated", { title: "pitchPublisherDeveloperProgramStudioCountryLocatedTitle" }],
  [
    "youHavePublisher",
    {
      title: "pitchPublisherDeveloperProgramYouHavePublisherTitle",
      mapper: (
        data: PitchInformationPublisherDeveloperDataType,
        localizedStrings: Map<string, string>
      ) => localizedStrings.get(youHavePublisherMap.get(data.youHavePublisher) ?? ""),
    },
  ],
  ["publisherName", { title: "pitchPublisherDeveloperProgramPublisherNameTitle" }],
  ["studioEstablished", { title: "pitchPublisherDeveloperProgramStudioEstablishedTitle" }],
  ["introductionDeckStudio", { title: "pitchPublisherDeveloperProgramIntroductionDeckTitle" }],
  ["companyWebsite", { title: "pitchPublisherDeveloperProgramCompanyWebsiteTitle" }],
  [
    "signedUpIdXbox",
    {
      title: "pitchPublisherDeveloperProgramAlreadySignedIdXboxTitle",
      mapper: (
        data: PitchInformationPublisherDeveloperDataType,
        localizedStrings: Map<string, string>
      ) => localizedStrings.get(alreadySignedToXboxMap.get(data.signedUpIdXbox) ?? ""),
    },
  ],
  [
    "kitAlready",
    {
      title: "pitchPublisherDeveloperProgramKitAlreadyTitle",
      mapper: (
        data: PitchInformationPublisherDeveloperDataType,
        localizedStrings: Map<string, string>
      ) => localizedStrings.get(kitAlreadyMap.get(data.kitAlready) ?? ""),
    },
  ],
  [
    "launchedForConsole",
    {
      title: "pitchPublisherDeveloperProgramLaunchedGameOnConsoleBeforeTitle",
      mapper: (
        data: PitchInformationPublisherDeveloperDataType,
        localizedStrings: Map<string, string>
      ) => localizedStrings.get(launchedForConsoleMap.get(data.launchedForConsole) ?? ""),
    },
  ],
  [
    "listGameLaunchedOnConsole",
    { title: "pitchPublisherDeveloperProgramGamesLaunchedForConsoleTitle" },
  ],
]);

export const ReadOnlyPitchPublisherDeveloper = ({
  dataInForm,
}: ReadOnlyPitchPublisherDeveloperProps) => {
  const extraStringsToLocalize = [
    "pitchPublisherDeveloperProgramOptionThreeTitle",
    "pitchPublisherDeveloperProgramOptionOneTitle",
    "pitchPublisherDeveloperProgramOptionTwoTitle",
    "pitchPublisherDeveloperProgramStudioSizeOneToFourthTitle",
    "pitchPublisherDeveloperProgramStudioSizeFiveToTenTitle",
    "pitchPublisherDeveloperProgramStudioSizeTenToFifteenTitle",
    "pitchPublisherDeveloperProgramStudioSizeFifteenToTwentyfiveTitle",
    "pitchPublisherDeveloperProgramStudioSizeTwentyfiveToFortyfiveTitle",
    "pitchPublisherDeveloperProgramStudioSizeFortyfiveToAboveTitle",
    "pitchPublisherDeveloperProgramYouHavePublisherOptionYesOption",
    "pitchPublisherDeveloperProgramYouHavePublisherOptionNoOption",
    "pitchPublisherDeveloperProgramYouHavePublisherOptionNegotiatingOption",
    "pitchPublisherDeveloperProgramYouHavePublisherOptionUnsureOption",
    "pitchPublisherDeveloperProgramYouHavePublisherOptionSelfPublishingOption",
    "pitchPublisherDeveloperProgramAlreadySignedIdXboxYesOption",
    "pitchPublisherDeveloperProgramAlreadySignedIdXboxNoOption",
    "pitchPublisherDeveloperProgramKitAlreadyYesOption",
    "pitchPublisherDeveloperProgramKitAlreadyNoOption",
    "pitchPublisherDeveloperProgramKitAlreadyUnknownOption",
    "yes",
    "no",
  ];
  return (
    <ReadOnlyForm
      formConfig={PitchPublisherDeveloperConfig}
      formData={dataInForm}
      extraStringsToLocalize={extraStringsToLocalize}
    />
  );
};
